import React, { useEffect, useState } from 'react';
import {
  Button,
  Container,
  Flex,
  Heading,
  Stack,
} from '@grubhubprod/gh-cookbook-react/dist';
import { Copy } from '@grubhubprod/gh-cookbook-react';
import { SignupStepFooter } from 'src/components/common/features/Signup/components/SignupStepFooter/SignupStepFooter';
import { useEnterpriseSignupStatus } from 'src/components/EnterpriseSignupStatusProvider/EnterpriseSignupStatusProvider';
import { EnterpriseBackToHomeButton } from 'src/components/EnterpriseBackToHomeButton/EnterpriseBackToHomeButton';
import { EnterpriseLocationTable } from 'src/components/EnterpriseSignupSteps/EnterpriseCompanyDetailsStep/EnterpriseLocationTable';
import { EnterpriseDepartmentTable } from 'src/components/EnterpriseSignupSteps/EnterpriseCompanyDetailsStep/EnterpriseDepartmentTable';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';

export function EnterpriseCompanyDetailsStep(): JSX.Element {
  const { goToNextStep } = useEnterpriseSignupStatus();
  const { isPhone } = useScreenSizes();
  const [isLocationsValid, setIsLocationsValid] = useState(false);
  const [isDepartmentsValid, setIsDepartmentsValid] = useState(false);
  const { corpData } = useAuth();
  const { sendTealiumPageView } = useAnalytics();

  useEffect(() => {
    sendTealiumPageView({
      pagegroup: 'corporate admin - signup flow',
      pagesubgroup: 'add company details',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function isValid(): boolean {
    if (corpData.locationsRequired && !isLocationsValid) {
      return false;
    }
    if (corpData.departmentsRequired && !isDepartmentsValid) {
      return false;
    }
    return true;
  }

  return (
    <>
      <Container variant="md">
        {!isPhone && (
          <Flex justifyContent="flex-end">
            <EnterpriseBackToHomeButton />
          </Flex>
        )}
        <Stack y="sm">
          <Heading variant="h3">
            First, let’s set up your company details
          </Heading>
        </Stack>
        <Stack y="md">
          <Copy variant="body">
            Sharing details about your company allows you to have more control
            over how employees order food and invoicing. You can manage
            locations and departments from your corporate admin portal.
          </Copy>
        </Stack>
        <Stack y="x-sm">
          <Heading variant="h6">
            Your locations {!corpData.locationsRequired && '(optional)'}
          </Heading>
        </Stack>
        <Stack y="sm">
          <Copy variant="body">
            A location is a place, like an office, where employees can order
            food from. If you plan on uploading a CSV with employees assigned to
            a location and want to include this information in invoices, add
            now.
          </Copy>
        </Stack>
        <EnterpriseLocationTable
          hasItemsCb={(isValid): void => setIsLocationsValid(isValid)}
        />
        <Stack y="x-sm">
          <Heading variant="h6">
            Your departments {!corpData.departmentsRequired && '(optional)'}
          </Heading>
        </Stack>
        <Stack y="sm">
          <Copy variant="body">
            If you plan on uploading a CSV with employees assigned to a
            department and want to include this information in invoices, add
            now, like Finance or HR.
          </Copy>
        </Stack>
        <EnterpriseDepartmentTable
          hasItemsCb={(isValid): void => setIsDepartmentsValid(isValid)}
        />
      </Container>
      <SignupStepFooter containerSize="md">
        <Flex justifyContent="flex-end">
          <Button
            disabled={!isValid()}
            variant="primary"
            buttonSize="medium"
            onClick={goToNextStep}
          >
            Save and continue
          </Button>
        </Flex>
      </SignupStepFooter>
    </>
  );
}
